import { AlertCircle } from "lucide-react";
import { useMemo, useRef, useState } from "react";

import { SectionLabel } from "components/AppShell";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

import {
  HERO_HEADLINE_MAX,
  HERO_KICKER_MAX,
  HERO_STAT_LABEL_MAX,
  HERO_STAT_VALUE_MAX,
  HERO_SUBLINE_MAX,
  splitHeadline,
  STAT_SLOTS,
  type StatSlot,
} from "../../events/landing";
import {
  firstProblemField,
  landingChanges,
  landingFormFrom,
  landingProblems,
  normalizeHeadline,
  type LandingForm,
} from "../landing-page-form";
import type { EventSettings } from "../schemas";
import { useEventSettingsWrite } from "../use-event-settings-writes";
import { LimitedField } from "./LimitedField";

/**
 * The words on the bold landing page's hero: a kicker, a headline of up to
 * three lines, a subline, and up to three number tiles.
 *
 * All of it is optional, and an event with none of it shows the plain hero.
 * The limits are the rules' (see `landing.ts`); they are checked on save, once
 * the admin has tried, so a tile is not called wrong while its second text is
 * still being typed. The counters are live.
 */
export function LandingPageCard({
  eventId,
  event,
  reload,
}: {
  eventId: string;
  event: EventSettings;
  reload: () => void;
}) {
  const { save, saving, error } = useEventSettingsWrite(eventId);
  const [form, setForm] = useState<LandingForm>(() => landingFormFrom(event));
  const [tried, setTried] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const card = useRef<HTMLDivElement>(null);

  const changes = useMemo(() => landingChanges(form, event), [form, event]);
  const dirty = Object.keys(changes).length > 0;
  const problems = useMemo(() => landingProblems(form), [form]);
  const shown = tried ? problems : {};

  function edit(next: Partial<LandingForm>) {
    setStatus(null);
    setForm({ ...form, ...next });
  }

  function editTile(slot: StatSlot, part: "value" | "label", text: string) {
    edit({
      stats: { ...form.stats, [slot]: { ...form.stats[slot], [part]: text } },
    });
  }

  async function handleSave() {
    setTried(true);
    setStatus(null);
    if (Object.keys(problems).length > 0) {
      // To the first wrong field, so a phone does not leave the organiser
      // looking at a card whose error is somewhere above the fold.
      const field = firstProblemField(form, problems);
      if (field) card.current?.querySelector<HTMLElement>(`#${field}`)?.focus();
      return;
    }
    if (await save(changes)) {
      // Errors belong to a save that was refused. Left on, a new tile would be
      // called wrong the moment its first text was typed.
      setTried(false);
      setStatus("Landing page saved");
      reload();
    }
  }

  return (
    <Card
      ref={card}
      className="border-border shadow-none"
      data-testid="landing-card"
    >
      <CardContent className="space-y-4 py-5">
        <SectionLabel>Landing page</SectionLabel>
        <p className="text-sm text-muted-foreground">
          The words at the top of the event page. Leave a field empty to leave
          it out; with all of them empty the page shows the plain header.
        </p>

        <LimitedField
          id="landing-kicker"
          label="Kicker"
          value={form.kicker}
          max={HERO_KICKER_MAX}
          count={form.kicker.trim().length}
          disabled={saving}
          placeholder="Holtzbrinck Innovation Days"
          hint="The small line above the headline."
          error={shown.kicker}
          onChange={(kicker) => edit({ kicker })}
        />
        <LimitedField
          id="landing-headline"
          label="Headline"
          value={form.headline}
          max={HERO_HEADLINE_MAX}
          count={normalizeHeadline(form.headline).length}
          multiline
          rows={3}
          disabled={saving}
          hint="Up to three lines. Press Enter to start a new line. The last line is drawn in the accent colour."
          error={shown.headline}
          onChange={(headline) => edit({ headline })}
        />
        <LimitedField
          id="landing-subline"
          label="Subline"
          value={form.subline}
          max={HERO_SUBLINE_MAX}
          count={form.subline.trim().length}
          multiline
          rows={2}
          disabled={saving}
          hint="One or two sentences under the headline. Line breaks are kept."
          error={shown.subline}
          onChange={(subline) => edit({ subline })}
        />

        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Number tiles</p>
          <p className="text-xs text-muted-foreground">
            Up to three short facts, such as 48 and hours. Each tile needs both
            texts, or none.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            {STAT_SLOTS.map((slot, index) => (
              <StatTileFields
                key={slot}
                slot={slot}
                name={`Tile ${index + 1}`}
                tile={form.stats[slot]}
                disabled={saving}
                error={shown.stats?.[slot]}
                onChange={(part, text) => editTile(slot, part, text)}
              />
            ))}
          </div>
        </div>

        <LandingPreview form={form} />

        {error && (
          <p
            role="alert"
            className="flex items-center gap-2 text-sm text-destructive-text"
            data-testid="landing-error"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </p>
        )}

        {/* Always rendered, and visually hidden rather than display:none while
            empty, so a screen reader has the live region before a message
            arrives; one that appears with its text is often not read. */}
        <p
          role="status"
          className={status ? "text-sm text-foreground" : "sr-only"}
          data-testid="landing-status"
        >
          {status}
        </p>

        <Button
          variant="secondary"
          onClick={() => void handleSave()}
          disabled={saving || !dirty}
          className="max-sm:w-full"
          data-testid="save-landing"
        >
          {saving ? "Saving…" : "Save landing page"}
        </Button>
      </CardContent>
    </Card>
  );
}

function StatTileFields({
  slot,
  name,
  tile,
  disabled,
  error,
  onChange,
}: {
  slot: StatSlot;
  name: string;
  tile: { value: string; label: string };
  disabled: boolean;
  error?: string;
  onChange: (part: "value" | "label", text: string) => void;
}) {
  const errorId = `landing-stat-${slot}-error`;
  return (
    <fieldset className="min-w-0 space-y-3 rounded-md border border-border p-3">
      <legend className="px-1 text-sm font-medium text-foreground">
        {name}
      </legend>
      <LimitedField
        id={`landing-stat-${slot}-value`}
        label="Number"
        value={tile.value}
        max={HERO_STAT_VALUE_MAX}
        count={tile.value.trim().length}
        disabled={disabled}
        placeholder="48"
        invalid={Boolean(error)}
        describedBy={error ? errorId : undefined}
        onChange={(text) => onChange("value", text)}
      />
      <LimitedField
        id={`landing-stat-${slot}-label`}
        label="Label"
        value={tile.label}
        max={HERO_STAT_LABEL_MAX}
        count={tile.label.trim().length}
        disabled={disabled}
        placeholder="hours"
        invalid={Boolean(error)}
        describedBy={error ? errorId : undefined}
        onChange={(text) => onChange("label", text)}
      />
      {error && (
        <p
          id={errorId}
          role="alert"
          className="flex items-start gap-2 text-sm text-destructive-text"
          data-testid={errorId}
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </p>
      )}
    </fieldset>
  );
}

/**
 * The hero words as they will sit on the page, in the page's own order and
 * with the last headline line in the accent. A sketch of the type, not of the
 * layout: the real page sets it over the event image.
 */
function LandingPreview({ form }: { form: LandingForm }) {
  const { lines, accentIndex } = splitHeadline(form.headline);
  const tiles = STAT_SLOTS.flatMap((slot) => {
    const value = form.stats[slot].value.trim();
    const label = form.stats[slot].label.trim();
    return value && label ? [{ slot, value, label }] : [];
  });
  const kicker = form.kicker.trim();
  const subline = form.subline.trim();
  const empty = !kicker && lines.length === 0 && !subline && !tiles.length;

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-foreground">Preview</p>
      <div
        className="space-y-3 rounded-2xl border border-border bg-dusk-hero p-5 text-white dark:border-edge dark:border-lit-edge"
        data-testid="landing-preview"
      >
        {empty && (
          <p className="text-sm text-white/70">
            Nothing yet. The page shows the plain header.
          </p>
        )}
        {kicker && (
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/80">
            {kicker}
          </p>
        )}
        {lines.length > 0 && (
          <p className="font-display text-3xl font-normal leading-tight">
            {lines.map((line, index) => (
              <span
                key={index}
                className={
                  index === accentIndex
                    ? "block font-semibold italic text-hb-yellow"
                    : "block"
                }
                data-accent={index === accentIndex ? "true" : "false"}
                data-testid={`landing-preview-line-${index}`}
              >
                {line}
              </span>
            ))}
          </p>
        )}
        {subline && (
          <p className="whitespace-pre-line text-sm text-white/90">{subline}</p>
        )}
        {tiles.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {tiles.map(({ slot, value, label }) => (
              <div
                key={slot}
                className="rounded-md border border-white/25 px-3 py-2"
                data-testid={`landing-preview-stat-${slot}`}
              >
                <p className="font-display text-xl font-semibold">{value}</p>
                <p className="text-xs text-white/80">{label}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
